import React, { useEffect, useState } from 'react';
import { Button, Col } from 'react-bootstrap';
import { Prompt } from 'react-router';
import { connect } from 'react-redux';
import { SubmissionError, submit } from 'redux-form';
import { createStructuredSelector } from 'reselect';
import { any, keys } from 'ramda';
import moment from 'moment';
import { injectIntl } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import DocumentTitle from 'react-document-title';
import * as actions from './actions';
import * as appActions from '../App/actions';
import * as selectors from './selectors';
import * as appSelectors from '../App/selectors';
import type { Props } from './types';
import Header from './Header';
import Form from './Form';
import { getSentAndExecutedStatus } from '../../utils/lease-helpers';
import type { EditResidentHouseholdFormValues } from './Form/types';
import validate from './Form/validate';
import { Spinner } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  ModalConfirm,
  useConfirmModal,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Banner,
  Grid,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { navigateToUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';
import { useEditHouseholdMemberDatesEnabled } from '../ResidentProfile/hooks';

import appMessages from '../App/messages';
import PetFeeModal from './PetFeeModal';
import { usePetFeeModal, usePetCharges } from './PetFeeModal/hooks';
import componentMessages from './messages';

const messages = {
  ...appMessages,
  ...componentMessages,
};

export const EditResidentHousehold = (props: Props) => {
  const {
    clearState,
    getHousehold,
    getAllPendingApplicants,
    getApplicantTypes,
    getPetBreeds,
    getPetTypes,
    getRelationshipTypes,
    getSuffixes,
    getAllAffordableRelationships,
    getLatestHouseholdHistory,
    getHouseholdLeasesByResidentId,
    getHouseholdHasOpenCerts,
    match,
    enablePrompt,
    householdHistory,
    leases,
    isHouseholdLoading,
    isHouseholdHistoryLoading,
    financiallyResponsibleNames,
    isApplicantTypesLoading,
    isPetTypesLoading,
    isPetBreedsLoading,
    isRelationshipsLoading,
    isAffordableRelationshipsLoading,
    isSuffixesLoading,
    intl,
    householdHasOpenCerts,
    selectedProperty,
    newPetsToChargeState,
  } = props;

  const [submitting, setSubmitting] = useState(false);
  const { addlContactInfoResidents, automatedLeaseChargesP1 } = useFlags();
  const isEditHouseholdMemberDatesEnabled =
    useEditHouseholdMemberDatesEnabled();
  const [petFeeFormValues, setPetFeeFormValues] = useState({});

  const { openConfirm, openConfirmModal, closeConfirmModal } =
    useConfirmModal();

  const { petCharges, isLoadingPetCharges, petRentConfigured } = usePetCharges({
    propertyId: selectedProperty?.id,
    organizationId: selectedProperty?.organizationId,
  });
  const petFeeModalProps = usePetFeeModal({
    newPetsToCharge: newPetsToChargeState,
    petCharges,
    isLoadingPetCharges,
  });

  const isLoading =
    isHouseholdLoading ||
    isHouseholdHistoryLoading ||
    isApplicantTypesLoading ||
    isPetTypesLoading ||
    isPetBreedsLoading ||
    isRelationshipsLoading ||
    isAffordableRelationshipsLoading ||
    isSuffixesLoading ||
    isLoadingPetCharges;

  const residentId = match?.params?.residentId;
  useEffect(() => {
    clearState();
    getHousehold(residentId);
    getAllPendingApplicants(residentId);
    getApplicantTypes();
    getPetBreeds();
    getPetTypes();
    getRelationshipTypes();
    getSuffixes();
    getAllAffordableRelationships();
    getLatestHouseholdHistory(residentId);
    getHouseholdLeasesByResidentId(residentId);
    getHouseholdHasOpenCerts(residentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getHousehold(residentId);
    getHouseholdHasOpenCerts(residentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residentId]);

  const onSaveClick = () => {
    const newPetsAdded = newPetsToChargeState.length > 0;
    // Recurring pet rent is configured separately from one-time pet
    // fees/deposits. The modal must still open when only pet rent applies so
    // the user is informed the pet rent charge will begin the following month.
    const petChargesExist = petCharges.length > 0 || petRentConfigured;
    if (automatedLeaseChargesP1 && newPetsAdded && petChargesExist) {
      petFeeModalProps.handleOpen({});
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    setSubmitting(true);
    setPetFeeFormValues(petFeeModalProps.reactHookFormProps.getValues());
    petFeeModalProps.handleClose();
    openConfirmModal();
  };

  const getPendingApplicantFormValues = (values: any) => {
    if (values.pendingPets) {
      return {
        pendingPets: [
          ...values.pendingPets.map((pet) => ({
            ...pet,
            petTypeId: pet.petTypeId === 'default' ? undefined : pet.petTypeId,
            weight: pet.weight === '' ? undefined : pet.weight,
            petTag: pet.petTag === '' ? undefined : pet.petTag,
          })),
        ],
      };
    }
    return { pendingPets: [] };
  };

  const getPrimaryPhoneNumber = (contactInformation: any) => {
    const primaryPhone = contactInformation?.phones?.find(
      (phone) => phone.isPrimary,
    );
    return primaryPhone?.phoneNumber;
  };

  const getPrimaryEmailAddress = (contactInformation: any) => {
    const primaryEmail = contactInformation?.emails?.find(
      (email) => email.isPrimary,
    );
    return primaryEmail?.emailAddress;
  };

  const getResidentFormFromValues = (values: any) => {
    const petFeesToCharge = Object.keys(petFeeFormValues)
      .map((propertyFeeId) => {
        if (petFeeFormValues[propertyFeeId] > 0) {
          return {
            propertyFeeId,
            count: petFeeFormValues[propertyFeeId],
          };
        } else {
          return null;
        }
      })
      .filter((item) => item !== null);

    return {
      ...values,
      adults: [
        ...values.adults.map((adult) => {
          const transformedAdult = {
            ...adult,
            suffix: adult.suffix === 'default' ? undefined : adult.suffix,
            relationshipId:
              adult.relationshipId === 'default'
                ? undefined
                : adult.relationshipId,
            affordableRelationshipId:
              adult.affordableRelationshipId === 'default'
                ? undefined
                : adult.affordableRelationshipId,
            isPrimary: values.primaryResidentId === adult.id,
          };

          if (addlContactInfoResidents) {
            // When feature flag is enabled, ensure legacy fields are populated from contactInformation
            if (adult.contactInformation) {
              const primaryPhone = getPrimaryPhoneNumber(
                adult.contactInformation,
              );
              const primaryEmail = getPrimaryEmailAddress(
                adult.contactInformation,
              );

              // Sync legacy fields with primary contact information
              if (primaryPhone) {
                transformedAdult.phoneNumber = primaryPhone;
              }
              if (primaryEmail) {
                transformedAdult.emailAddress = primaryEmail;
              }
            }
          } else {
            // When feature flag is disabled, sync legacy fields INTO contactInformation
            // to ensure backend receives consistent data
            if (adult.contactInformation) {
              // Update contactInformation to match legacy field values
              const updatedContactInfo = { ...adult.contactInformation };

              // Sync phone number from legacy field to contactInformation
              if (
                transformedAdult.phoneNumber &&
                updatedContactInfo.phones?.length > 0
              ) {
                updatedContactInfo.phones = updatedContactInfo.phones.map(
                  (phone, index) => ({
                    ...phone,
                    phoneNumber:
                      index === 0 || phone.isPrimary
                        ? transformedAdult.phoneNumber
                        : phone.phoneNumber,
                  }),
                );
              } else if (transformedAdult.phoneNumber) {
                // Create phone entry if none exists
                updatedContactInfo.phones = [
                  {
                    phoneNumber: transformedAdult.phoneNumber,
                    phoneType: 'mobile',
                    isPrimary: true,
                    isValidated: false,
                  },
                ];
              }

              // Sync email address from legacy field to contactInformation
              if (
                transformedAdult.emailAddress &&
                updatedContactInfo.emails?.length > 0
              ) {
                updatedContactInfo.emails = updatedContactInfo.emails.map(
                  (email, index) => ({
                    ...email,
                    emailAddress:
                      index === 0 || email.isPrimary
                        ? transformedAdult.emailAddress
                        : email.emailAddress,
                  }),
                );
              } else if (transformedAdult.emailAddress) {
                // Create email entry if none exists
                updatedContactInfo.emails = [
                  {
                    emailAddress: transformedAdult.emailAddress,
                    emailType: 'personal',
                    isPrimary: true,
                    isValidated: false,
                    isOptedOut: false,
                  },
                ];
              }

              transformedAdult.contactInformation = updatedContactInfo;
            }
          }

          return transformedAdult;
        }),
      ],
      minors: [
        ...values.minors.map((minor) => ({
          ...minor,
          relationshipId:
            minor.relationshipId === 'default'
              ? undefined
              : minor.relationshipId,
          affordableRelationshipId:
            minor.affordableRelationshipId === 'default'
              ? undefined
              : minor.affordableRelationshipId,
        })),
      ],
      pets: [
        ...values.pets.map((pet) => ({
          ...pet,
          petTypeId: pet.petTypeId === 'default' ? undefined : pet.petTypeId,
          breedId: pet.breedId === 'default' ? undefined : pet.breedId,
          petTag: pet.petTag === '' ? undefined : pet.petTag,
          weight: pet.weight === '' ? undefined : pet.weight,
        })),
      ],
      petFeesToCharge,
    };
  };

  const onSubmit = (values: EditResidentHouseholdFormValues) => {
    const errors = validate(
      values,
      props.petTypeOptions,
      props.applicantTypesOptions,
      props.intl,
    );
    const hasKeys = (o) => keys(o).length > 0;

    // Do not block submission with errors for fields hidden on this surface.
    const blockingErrors = isEditHouseholdMemberDatesEnabled
      ? []
      : [...errors.pets, ...errors.pendingPets, ...errors.adults];

    // Blocking errors correspond to visible fields.
    if (any(hasKeys)(blockingErrors)) throw new SubmissionError(errors);
    const residentToUpdate = getResidentFormFromValues(values);
    const pendingApplicantsToUpdate = getPendingApplicantFormValues(values);
    props.updatePendingApplicants(
      props.match.params.residentId,
      pendingApplicantsToUpdate,
    );
    props.editHousehold(
      props.match.params.residentId,
      residentToUpdate,
      isEditHouseholdMemberDatesEnabled,
    );
  };
  const onCancel = () => {
    navigateToUrlWithSelectedPropertyId(
      `/resident/${props.match.params.residentId}`,
    );
  };

  const lease = leases?.length ? leases[0] : {};

  const { leaseExecuted, leaseSentToPortal } = getSentAndExecutedStatus(lease);

  const lastEditedAt = moment
    .utc(householdHistory?.createdAt)
    .local()
    .format('MMM D, YYYY @ LT');

  if (isLoading) {
    return (
      <Spinner
        sx={{
          height: '100vh',
          width: '100%',
          position: 'fixed',
          zIndex: 1,
        }}
      />
    );
  }

  return (
    <DocumentTitle title={props.pageTitle}>
      <div className="container-fluid">
        <Prompt
          message={intl.formatMessage(messages.closeWithUnsavedChanges)}
          when={enablePrompt}
        />
        <Header
          financiallyResponsibleNames={financiallyResponsibleNames}
          residentId={residentId}
          leaseExecuted={leaseExecuted}
          leaseSentToPortal={leaseSentToPortal}
        />
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={2}
        >
          {householdHasOpenCerts && (
            <Grid item xs={12}>
              <Banner
                color="warning"
                text={intl.formatMessage(messages.editHouseholdBlocked)}
                BoxProps={{
                  sx: {
                    width: '100%',
                    maxWidth: 'none',
                  },
                }}
              />
            </Grid>
          )}
          {!isHouseholdHistoryLoading && (
            <Grid item xs={12}>
              <Banner
                color="blue"
                text={intl.formatMessage(messages.lastEditToPrimary, {
                  dateTime: lastEditedAt,
                })}
                BoxProps={{ sx: { width: '100%', maxWidth: 'none' } }}
              />
            </Grid>
          )}
        </Grid>
        {!isHouseholdLoading && (
          <Form
            onSubmit={onSubmit}
            leaseLocked={
              householdHasOpenCerts || leaseExecuted || leaseSentToPortal
            }
            householdHasOpenCerts={householdHasOpenCerts}
            isConventionalProperty={
              props.propertyClassType === 'Conventional' ||
              !props.isAffordableApplication
            }
          />
        )}
        <Col xs={6}>
          <Button
            data-testid="cancel"
            className="pull-right"
            onClick={onCancel}
          >
            {intl.formatMessage(messages.cancel)}
          </Button>
        </Col>
        <Col xs={6}>
          <Button
            bsStyle="primary"
            className="pull-left"
            onClick={onSaveClick}
            disabled={householdHasOpenCerts || submitting}
          >
            {intl.formatMessage(messages.saveChanges)}
          </Button>
        </Col>
        <>
          <PetFeeModal
            handleSubmit={handleSubmit}
            petsToCharge={newPetsToChargeState}
            petRentConfigured={petRentConfigured}
            leaseMonthlyPetRent={lease?.monthlyPetRent ?? null}
            intl={intl}
            {...petFeeModalProps}
          />
          <ModalConfirm
            title={intl.formatMessage(messages.confirmSave)}
            open={openConfirm}
            onClose={closeConfirmModal}
            actionsProps={[
              {
                children: intl.formatMessage(messages.no),
                onClick: () => {
                  closeConfirmModal();
                  setSubmitting(false);
                },
              },
              {
                children: intl.formatMessage(messages.yes),
                onClick: () => {
                  closeConfirmModal();
                  props.submit('editResidentHousehold');
                },
              },
            ]}
          />
        </>
      </div>
    </DocumentTitle>
  );
};

export default connect(
  createStructuredSelector({
    pageTitle: selectors.getPageTitle,
    isHouseholdLoading: selectors.isHouseholdLoading,
    enablePrompt: selectors.enablePrompt,
    petTypeOptions: appSelectors.getPetTypeOptions,
    isHouseholdHistoryLoading: selectors.isHouseholdHistoryLoading,
    householdHistory: selectors.getHouseholdHistory,
    applicantTypesOptions: appSelectors.getApplicantTypeOptions,
    leases: selectors.getHouseholdLeases,
    financiallyResponsibleNames: selectors.getFinanciallyResponsibleNames,
    isApplicantTypesLoading: selectors.isApplicantTypesLoading,
    isPetTypesLoading: selectors.isPetTypesLoading,
    isPetBreedsLoading: selectors.isPetBreedsLoading,
    isRelationshipsLoading: selectors.isRelationshipsLoading,
    isAffordableRelationshipsLoading:
      selectors.isAffordableRelationshipsLoading,
    isSuffixesLoading: selectors.isSuffixesLoading,
    householdHasOpenCerts: selectors.getHouseholdHasOpenCerts,
    propertyClassType: appSelectors.getSelectedPropertyClassType,
    isAffordableApplication: selectors.isAffordableApplication,
    selectedProperty: appSelectors.selectSelectedProperty,
    newPetsToChargeState: selectors.getNewPetsToChargeState,
  }),
  {
    clearState: actions.clearState,
    getHousehold: actions.getResidentHousehold,
    getAllPendingApplicants: actions.getAllPendingApplicants,
    editHousehold: actions.editResidentHousehold,
    updatePendingApplicants: actions.updatePendingApplicants,
    getApplicantTypes: appActions.getAllApplicantTypes,
    getPetBreeds: appActions.getAllPetBreeds,
    getPetTypes: appActions.getAllPetTypes,
    getRelationshipTypes: appActions.getAllRelationships,
    getAllAffordableRelationships: appActions.getAllAffordableRelationships,
    getSuffixes: appActions.getAllNameSuffixes,
    getLatestHouseholdHistory: actions.getLatestHouseholdHistory,
    getHouseholdLeasesByResidentId: actions.getHouseholdLeasesByResidentId,
    getHouseholdHasOpenCerts: actions.getHouseholdHasOpenCerts,
    submit,
  },
)(injectIntl(EditResidentHousehold));
